import {
  Alert,
  Checkbox,
  FormControl,
  FormControlLabel,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { Formik } from "formik";
import { useMemo } from "react";
import * as Yup from "yup";

import { VisualModelHelper } from "@/common/helpers/entity/vehicleVisualModel";
import { SvgHelper } from "@/common/helpers/svg";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import useMounted from "@/common/hooks/mount/useMounted";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { Mapper } from "@/common/mapping/mapper";
import { BaseFormikValues } from "@/common/ts/error";
import { ValidationHelper } from "@/common/validation";
import { apiClient } from "@/core/api/ApiClient";
import {
  VehicleArea,
  VehicleDamageDto,
  VehicleDamageMigrateToNewVisualModelDto,
  VehicleProjection,
  VehicleVisualModelDto,
} from "@/core/api/generated/v1";

import NoDataAlert from "../../AppAlerts/NoDataAlert";
import AppButton from "../../Button/AppButton";
import GeneralValidationError from "../../Error/GeneralValidationError";
import AppTypography from "../../Text/AppTypography";
import VehicleVisualModelAutocomplete from "../VehicleVisualModel/VehicleVisualModelAutocomplete";
import { VisualModelViewer } from "../VehicleVisualModel/Viewer/v2/VisualModelViewer";

interface OwnProps {
  vehicleDamage: VehicleDamageDto;
  onCancel: () => void;
  onSave: (newValue: VehicleDamageDto) => void;
}

export type VehicleDamageMigrateToNewVisualModelProps = OwnProps;

export default function VehicleDamageMigrateToNewVisualModel({
  vehicleDamage,
  onCancel,
  onSave,
}: VehicleDamageMigrateToNewVisualModelProps) {
  const mounted = useMounted();
  const { enqueueSnackbar } = useAppSnackbar();
  // const transformComponentRef = useRef<ReactZoomPanPinchRef | null>(null);

  const vehicleVisualModelLatestOrDefaultRequest = useApiRequest(
    apiClient.vehicleVisualModelsApi.apiV1ReferenceDataVehiclesVisualModelsLatestOrDefaultGet,
    {
      vehicleId: vehicleDamage.vehicle?.id || undefined,
      isIgnoreDefaultForVehicle: true,
    },
    {
      deps: [vehicleDamage.vehicle?.id],
    },
  );
  const vehicleVisualModelLatestOrDefault = vehicleVisualModelLatestOrDefaultRequest?.data;

  const oldVehicleVisualModelRequest = useApiRequest(
    apiClient.vehicleVisualModelsApi.apiV1ReferenceDataVehiclesVisualModelsModelIdGet,
    {
      modelId: vehicleDamage.visualModel?.id || "",
    },
    {
      deps: [vehicleDamage.visualModel?.id],
    },
  );
  const oldVehicleVisualModel = oldVehicleVisualModelRequest?.data;

  const oldVisualPoint = useMemo(
    () => Mapper.visualModel.mapVehicleDamageDtoToVisualModelPoint(vehicleDamage),
    [vehicleDamage],
  );

  if (oldVehicleVisualModelRequest.isLoading || !oldVehicleVisualModelRequest.isFirstEnded) {
    return <LinearProgress />;
  }

  return (
    <Stack spacing={1}>
      <Alert severity='info'>
        To migrate the damage to new vehicle visual model, manually select new visual model and
        place damage point on correct position.
      </Alert>

      <Formik<
        VehicleDamageMigrateToNewVisualModelDto &
          BaseFormikValues & {
            oldVehicleVisualModel: Nil<VehicleVisualModelDto>;
            newVehicleVisualModel: Nil<VehicleVisualModelDto>;
          }
      >
        enableReinitialize
        initialValues={{
          oldVehicleVisualModelId: vehicleDamage.visualModel?.id,
          newVehicleVisualModelId:
            vehicleVisualModelLatestOrDefault &&
            vehicleVisualModelLatestOrDefault.id !== oldVehicleVisualModel?.id
              ? vehicleVisualModelLatestOrDefault.id
              : undefined,

          oldPoint: vehicleDamage?.point ? { ...vehicleDamage.point } : undefined,
          newPoint: vehicleDamage?.point ? { ...vehicleDamage.point } : undefined,
          isMigrateDamageDetection: true,

          oldVehicleVisualModel: oldVehicleVisualModel,
          newVehicleVisualModel:
            vehicleVisualModelLatestOrDefault &&
            vehicleVisualModelLatestOrDefault.id !== oldVehicleVisualModel?.id
              ? vehicleVisualModelLatestOrDefault
              : undefined,

          submit: "",
        }}
        validationSchema={Yup.object().shape({
          // vehicleId: Yup.string().required("Vehicle is required"),
        })}
        onSubmit={async (values, { setFieldError, setStatus, setSubmitting }) => {
          try {
            const response =
              await apiClient.vehicleDamagesApi.apiV1VehiclesVehicleIdDamagesVehicleDamageIdMigrateToNewVisualModelPost(
                {
                  nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                  vehicleId: vehicleDamage.vehicle?.id || "",
                  vehicleDamageId: vehicleDamage.id || "",
                  vehicleDamageMigrateToNewVisualModelDto: {
                    oldVehicleVisualModelId: values.oldVehicleVisualModelId,
                    newVehicleVisualModelId: values.newVehicleVisualModelId,
                    oldPoint: values.oldPoint,
                    newPoint: values.newPoint,
                    isMigrateDamageDetection: values.isMigrateDamageDetection,
                  },
                },
              );
            enqueueSnackbar("Saved.", {
              variant: "success",
            });
            onSave && onSave(response.data);

            if (mounted.current) {
              setStatus({ success: true });
              setSubmitting(false);
            }
          } catch (err: any) {
            if (mounted.current) {
              ValidationHelper.handleApiErrorResponseFormik(err, setFieldError);
              setStatus({ success: false });
              setSubmitting(false);
            }
          }
        }}
      >
        {({ errors, handleSubmit, isSubmitting, values, setFieldValue }) => {
          const newVisualPoint = values.newPoint
            ? Mapper.visualModel.mapVehicleDamageDtoToVisualModelPoint(vehicleDamage, {
                coords:
                  SvgHelper.mapGeneralSvgPointDtoToSvgPointFullCoordsOrNull(values.newPoint) ||
                  undefined,
              })
            : null;

          return (
            <form noValidate onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <Stack spacing={2}>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 4 }}>
                    {/* Old model */}
                    <Stack spacing={1}>
                      <Typography variant='h6'>Old visual model</Typography>

                      {!values.oldVehicleVisualModel && (
                        <NoDataAlert title='Old visual model not found for this damage.' />
                      )}

                      <Box>
                        <FormControl fullWidth margin='dense'>
                          <VehicleVisualModelAutocomplete
                            entity={values.oldVehicleVisualModel}
                            disabled
                          />
                        </FormControl>

                        {values.oldVehicleVisualModel && (
                          <Box>
                            <VisualModelViewer
                              visualModelId={values.oldVehicleVisualModel?.id}
                              visualModel={values.oldVehicleVisualModel}
                              points={oldVisualPoint ? [oldVisualPoint] : []}
                              initialMainImageSelector={{
                                area: vehicleDamage.area || VehicleArea.Exterior,
                                projection: vehicleDamage.projection || VehicleProjection.Front,
                              }}
                              variant='compact'
                              direction='column'
                              isFetchVisualModel={false}
                              isSwitchImageEnabled={false}
                              mainViewerProps={{
                                svgViewerProps: {
                                  isZoomEnabled: true,
                                  isResizeEnabled: true,
                                  isPointDragEnabled: false,
                                  svgProps: {
                                    width: { xxs: 300, md: 500 },
                                    height: { xxs: 300, md: 500 },
                                    minWidth: { xxs: 100, md: 300 },
                                    minHeight: { xxs: 100, md: 300 },
                                    maxWidth: { xxs: 400, md: 500 },
                                    maxHeight: { xxs: 400, md: 500 },
                                    cursor: "default",
                                    point: {
                                      cursor: "default",
                                    },
                                  },
                                  defaultPointStyle: {
                                    ...VisualModelHelper.defaultSvgPointStyleForDamage,
                                  },
                                },
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    </Stack>

                    {/* New model */}
                    <Stack spacing={1}>
                      <Typography variant='h6'>New visual model</Typography>

                      <Box>
                        <FormControl fullWidth margin='dense'>
                          <VehicleVisualModelAutocomplete
                            entity={undefined}
                            entityId={values.newVehicleVisualModelId}
                            searchFilters={{
                              excludeIds: values.oldVehicleVisualModelId
                                ? [values.oldVehicleVisualModelId]
                                : undefined,
                            }}
                            onChange={(newValue) => {
                              setFieldValue("newVehicleVisualModelId", newValue?.id);
                              setFieldValue("newVehicleVisualModel", newValue);
                            }}
                          />
                        </FormControl>

                        {values.newVehicleVisualModel && (
                          <Box>
                            <VisualModelViewer
                              visualModelId={undefined}
                              visualModel={values.newVehicleVisualModel}
                              points={newVisualPoint ? [newVisualPoint] : []}
                              initialMainImageSelector={{
                                area: newVisualPoint?.meta.area || VehicleArea.Exterior,
                                projection:
                                  newVisualPoint?.meta.projection || VehicleProjection.Front,
                              }}
                              variant='compact'
                              direction='column'
                              isFetchVisualModel={false}
                              isSwitchImageEnabled={false}
                              mainViewerProps={{
                                svgViewerProps: {
                                  isZoomEnabled: true,
                                  isResizeEnabled: true,
                                  isPointDragEnabled: true,
                                  svgProps: {
                                    width: { xxs: 300, md: 500 },
                                    height: { xxs: 300, md: 500 },
                                    minWidth: { xxs: 100, md: 300 },
                                    minHeight: { xxs: 100, md: 300 },
                                    maxWidth: { xxs: 400, md: 500 },
                                    maxHeight: { xxs: 400, md: 500 },
                                    cursor: "pointer",
                                    point: {
                                      cursor: "pointer",
                                    },
                                  },
                                  defaultPointStyle: {
                                    ...VisualModelHelper.defaultSvgPointStyleForDamage,
                                  },
                                },
                                onNewPointClicked: (params) => {
                                  setFieldValue(
                                    "newPoint",
                                    SvgHelper.mapSvgPointFullCoordsToGeneralSvgPointDto(
                                      params.coords,
                                    ),
                                  );
                                },
                                onPointMoved: (params) => {
                                  setFieldValue(
                                    "newPoint",
                                    SvgHelper.mapSvgPointFullCoordsToGeneralSvgPointDto(
                                      params.endCoords,
                                    ),
                                  );
                                },
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    </Stack>
                  </Box>

                  <FormControl margin='dense' fullWidth>
                    <FormControlLabel
                      sx={{ margin: 0 }}
                      disableTypography
                      control={
                        <Checkbox
                          checked={values.isMigrateDamageDetection ?? false}
                          onChange={(e) => {
                            setFieldValue("isMigrateDamageDetection", e.target.checked);
                          }}
                        />
                      }
                      label={
                        <AppTypography
                          decoration={{ variant: "helpText" }}
                          tooltipProps={{
                            title:
                              "Migrate source damage detection (only if the damage detection contains only this damage and has the same visual model).",
                          }}
                        >
                          Migrate damage detection
                        </AppTypography>
                      }
                    />
                  </FormControl>
                </Stack>

                <GeneralValidationError sx={{ my: 1 }} errors={errors} />

                <Stack
                  direction={{ xxs: "column", sm: "row" }}
                  gap={1}
                  sx={{
                    justifyContent: { xxs: "flex-start", sm: "flex-end" },
                    alignItems: { xxs: "flex-start", sm: "flex-start" },
                  }}
                >
                  <AppButton
                    variant='outlined'
                    size='medium'
                    color='text'
                    onClick={() => {
                      onCancel();
                    }}
                  >
                    Cancel
                  </AppButton>

                  <AppButton
                    disabled={!values.newVehicleVisualModelId}
                    loading={isSubmitting}
                    type='submit'
                    variant='contained'
                    size='medium'
                    color='primary'
                  >
                    Migrate
                  </AppButton>
                </Stack>
              </Stack>
            </form>
          );
        }}
      </Formik>
    </Stack>
  );
}
